import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import "katex/dist/katex.min.css?__remix_sideEffect__";
import "./styles/Latex.css?__remix_sideEffect__";
import "react-image-lightbox/style.css?__remix_sideEffect__";

import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpLink,
  InMemoryCache } from
"@apollo/client/index.js";
import { ClientStyleContext, ServerStyleContext } from "./context";
import {
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useLoaderData,
  useRouteError } from
"@remix-run/react";
import React, { useContext, useEffect } from "react";
import { clearAllCookies, getCookie, setCookie } from "./utils/TutorConstants";
import {
  customTheme,
  isEmptyOrNull,
  isNotEmptyOrNull,
  isUserLogin,
  storeImage,
  urlString } from
"./utils/common-utils";
import { useSimpleAutoRefresh } from "./api/AutoRefresh";
import AppBar from "./components/molecules/AppBar";
import BottomBar from "./components/molecules/bottom-bar/BottomBar";
import { ChakraProvider } from "@chakra-ui/react";
import { EditProfile } from "./components/molecules/profile/EditProfile";
import ErrorBoundaryGif from "./assets/ErrorBoundary.gif";
import Login from "./routes/login";
import ProfileTopMenu from "./components/molecules/profile/ProfileTopMenu";
import { RootProvider } from "./context/RootContext";
import Theme from "./theme";
import { TutorProvider } from "./context/AiTutorContext";
import { cssBundleHref } from "@remix-run/css-bundle";
import { json } from "@remix-run/node";
import styles from "../app/styles/index.module.css";
import { useLocation } from "react-router-dom";
import { useNavigate } from "@remix-run/react";
import useStateRef from "react-usestateref";
import { withEmotionCache } from "@emotion/react";
import { StorageService } from "./utils/StorageUtils";
import { redirectToLoginPage } from "./api/login-service";
import { AudioProvider } from "./context/AudioContext";
import OfflineComponent from "./components/molecules/offline/OfflineComponent";
import WifiDownImg from "./assets/svgs/wifiDownIcon.svg";
import { TourProvider } from "./context/TourContext";

export async function loader({ request }) {
  const url = new URL(request.url);
  const params = url.searchParams;
  const item = params?.get("item");
  const decodedItem = item ? decodeURIComponent(item) : "";
  const id = params?.get("id");
  const emailUserId = params?.get("userId");
  const decodedEmailUserId = emailUserId ? decodeURIComponent(emailUserId) : "";
  const taskProgressItem = id ? decodeURIComponent(id) : "";
  return json({
    ENV: {
      TUTOR_API_URL: process.env.TUTOR_API_URL,
      REACT_APP_AUTH_URL: process.env.REACT_APP_AUTH_URL,
      GRAPHQL_API_URL: process.env.GRAPHQL_API_URL,
      IS_STUDENTAPP_MAPPING: process.env.IS_STUDENTAPP_MAPPING,
      IS_DEFAULT_STANDALONE: process.env.IS_DEFAULT_STANDALONE,
      GP_RANDOM: process.env.GP_RANDOM,
      GP_RANDOM_QUESTION_COUNT: process.env.GP_RANDOM_QUESTION_COUNT,
      SCHOOL_SPECIFIC_LOGIN: process.env.SCHOOL_SPECIFIC_LOGIN,
      ENABLE_GP_MEDIA: process.env.ENABLE_GP_MEDIA || false,
      ASSETS_BUILD_DIRECTORY: process.env.ASSETS_BUILD_DIRECTORY,
      PUBLIC_PATH: process.env.PUBLIC_PATH,
      CHAT_VIEW_URL: process.env.CHAT_VIEW_URL,
      ENABLE_SUGGESTIONS: process.env.ENABLE_SUGGESTIONS || false,
      HASH_SECRET_KEY: process.env.HASH_SECRET_KEY,
      SENTRY_DSN: process.env.SENTRY_DSN,
      SUGGESTION_API_TRIGGER_LIMIT:
      process.env.SUGGESTION_API_TRIGGER_LIMIT || 3,
      USE_ELEVEN_LABS: process.env.USE_ELEVEN_LABS || false,
      ELEVEN_LABS_MODEL_NAME:
      process.env.ELEVEN_LABS_MODEL_NAME || "eleven_flash_v2",
      ENABLE_VOICE: process.env.ENABLE_VOICE || false,
      IS_TEXT_HILIGHT: process.env.IS_TEXT_HILIGHT || false,
      ALLOW_HANDWRITTEN: process.env.ALLOW_HANDWRITTEN || false,
      REACT_APP_GOOGLE_ANALYTICS_KEY:
      process.env.REACT_APP_GOOGLE_ANALYTICS_KEY || false,
      BROWSER_URL: process.env.BROWSER_URL || "https://coschool.ai",
      SHOW_HINT_BUTTON_AFTER: process.env.SHOW_HINT_BUTTON_AFTER || 2,
      SHOW_CONCLUDE_BUTTON_AFTER: process.env.SHOW_CONCLUDE_BUTTON_AFTER || 4,
      ENABLE_JOURNEY_EXECUTION: process.env.ENABLE_JOURNEY_EXECUTION || false,
      IS_STREAMING: process.env.IS_STREAMING || false,
      OCR_UPLOAD_SIZE_LIMIT: process.env.OCR_UPLOAD_SIZE_LIMIT || 3
    },
    decodedItem,
    taskProgressItem,
    decodedEmailUserId,
    isOnline: typeof navigator !== "undefined" ? navigator?.onLine : true
  });
}

// export const meta = () => {
//   return [
//     { title: "SchoolAI Web" },
//     { name: "description", content: "SchoolAI Web" },
//   ];
// };

export const links = () => [
{
  rel: "icon",
  type: "image/png",
  href: "https://cdn.innerscore.com/coschool.png"
},
{ rel: "preconnect", href: "https://fonts.googleapis.com" },
{ rel: "preconnect", href: "https://fonts.gstatic.com" },
{
  rel: "stylesheet",
  href: "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600&display=swap"
},
...(cssBundleHref ? [{ rel: "stylesheet", href: cssBundleHref }] : [])];


export function ErrorBoundary() {
  const navigate = useNavigate();
  const error = useRouteError();
  const location = useLocation();

  if (isRouteErrorResponse(error) || error) {
    return (
      <div
        style={{
          marginTop: "100px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center"
        }}>

        <img
          src={ErrorBoundaryGif}
          alt="Error Image"
          style={{
            width: "400px",
            height: "400px",
            maxWidth: "100%",
            margin: "0 auto"
          }} />

        <h1
          style={{ margin: "12px 20px", fontSize: "2rem", textAlign: "center" }}>

          Oops!!! Something went wrong... We're working on it.
        </h1>
        <button
          style={{
            backgroundColor: "green",
            color: "white",
            padding: "10px 20px",
            border: "none",
            cursor: "pointer",
            width: "250px",
            textAlign: "center"
          }}
          onClick={() => {
            navigate(`${urlString}/home`);
          }}>

          Go to Home
        </button>
      </div>);

  }
  captureRemixErrorBoundaryError(error);
  return null;
}

const Document = withEmotionCache(({ children, data }, emotionCache) => {
  const serverStyleData = useContext(ServerStyleContext);
  const clientStyleData = useContext(ClientStyleContext);

  useEffect(() => {
    emotionCache.sheet.container = document.head;
    const tags = emotionCache.sheet.tags;
    emotionCache.sheet.flush();
    tags.forEach((tag) => {
      emotionCache.sheet._insertTag(tag);
    });
    clientStyleData?.reset();
  }, []);

  const renderGoogleAnalytics = () => {
    // console.log("ENV 2", data?.ENV?.REACT_APP_GOOGLE_ANALYTICS_KEY);
    if (!data?.ENV?.REACT_APP_GOOGLE_ANALYTICS_KEY) return null;

    // console.log("ENV", data?.ENV?.REACT_APP_GOOGLE_ANALYTICS_KEY);
    return (
      <>
        <script
          async
          src={`https://www.googletagmanager.com/gtag/js?id=${data.ENV.REACT_APP_GOOGLE_ANALYTICS_KEY}`} />

        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${data.ENV.REACT_APP_GOOGLE_ANALYTICS_KEY}', {
                page_path: window.location.pathname,
              });
            `
          }} />

      </>);

  };

  return (
    <html lang="en">
      <head>
        <Meta />
        <meta charSet="utf-8" />
        <meta name="application-name" content="Coschool Student" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content="Coschool Student" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="theme-color" content="#FFFFFF" />

        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="msapplication-TileImage" content="/favIcon.png" />
        <meta name="msapplication-TileColor" content="#FFFFFF"></meta>

        {serverStyleData?.map(({ key, ids, css }) =>
        <style
          key={key}
          data-emotion={`${key} ${ids.join(" ")}`}
          dangerouslySetInnerHTML={{ __html: css }} />

        )}
        <Links />
        {/* Apple Touch Icons */}
        <link
          rel="apple-touch-icon"
          href="https://cdn.innerscore.com/coschool.png" />

        <link
          rel="apple-touch-icon"
          sizes="152x152"
          href="https://cdn.innerscore.com/coschool.png" />

        <link
          rel="apple-touch-icon"
          sizes="167x167"
          href="https://cdn.innerscore.com/coschool.png" />

        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="https://cdn.innerscore.com/coschool.png" />

        {/* Basic Favicons */}
        <link
          rel="icon"
          type="image/png"
          href="https://cdn.innerscore.com/coschool.png" />

        <link
          rel="icon"
          type="image/png"
          href="https://cdn.innerscore.com/coschool.png" />

        <link
          rel="shortcut icon"
          type="image/png"
          sizes="512x512"
          href="https://cdn.innerscore.com/coschool.png" />

        <link
          rel="icon"
          type="image/x-icon"
          href="https://cdn.innerscore.com/coschool.png" />


        {/* Modern Browsers */}
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="https://cdn.innerscore.com/coschool.png" />

        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="https://cdn.innerscore.com/coschool.png" />


        {/* Android Specific Icons */}
        <link
          rel="icon"
          type="image/png"
          sizes="36x36"
          href="https://cdn.innerscore.com/coschool.png" />

        <link
          rel="icon"
          type="image/png"
          sizes="48x48"
          href="https://cdn.innerscore.com/coschool.png" />

        <link
          rel="icon"
          type="image/png"
          sizes="72x72"
          href="https://cdn.innerscore.com/coschool.png" />

        <link
          rel="icon"
          type="image/png"
          sizes="96x96"
          href="https://cdn.innerscore.com/coschool.png" />

        <link
          rel="icon"
          type="image/png"
          sizes="128x128"
          href="https://cdn.innerscore.com/coschool.png" />

        <link
          rel="icon"
          type="image/png"
          sizes="192x192"
          href="https://cdn.innerscore.com/coschool.png" />

        <link
          rel="icon"
          type="image/png"
          sizes="512x512"
          href="https://cdn.innerscore.com/coschool.png" />

        {/* <link rel="manifest" href="/manifest.json" /> */}
        {renderGoogleAnalytics()}
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
        <LiveReload />
      </body>
    </html>);

});

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentVersion, setCurrentVersion, currentVersionRef] =
  useStateRef("");
  const [isLogin, setIsLogin, isLoginRef] = useStateRef(false);

  useSimpleAutoRefresh(300000);

  if (
  typeof window !== "undefined" &&
  typeof window.navigator !== "undefined")
  {
    const { ClientJS } = require("clientjs");
    const clientFingerPrint = new ClientJS();
    const fingerprint = clientFingerPrint.getFingerprint();
    if (fingerprint) {
      setCookie("fingerprint", fingerprint);
    }
  }

  const data = useLoaderData();
  try {
    if (isNotEmptyOrNull(data?.decodedItem)) {
      setCookie("StudentRef", data?.decodedItem);
    } else {
      if (
      isEmptyOrNull(getCookie("StudentRef")) &&
      typeof document !== "undefined")
      {
        const referrer = document?.referrer || "Direct Access";
        if (typeof window !== "undefined" && isNotEmptyOrNull(referrer)) {
          // console.log("document?.referrer", referrer);
          setCookie("StudentRef", referrer);
        }
      }
    }
  } catch (e) {
    console.log("error in decoded item", e);
  }

  useEffect(() => {
    try {
      if (isNotEmptyOrNull(data?.taskProgressItem)) {
        // Prevent redundant navigation
        setCookie("taskProgressId", data?.taskProgressItem);
        setCookie("emailUserId", data?.decodedEmailUserId);
        if (location.pathname !== `${urlString}/home` && isLoginRef?.current) {
          navigate(`${urlString}/home`, {
            state: {
              emailTaskProgressId: data?.taskProgressItem,
              emailUserId: data?.decodedEmailUserId
            }
          });
        }
      } else {
      }
    } catch (e) {
      console.log("couldn't fetch task progress id", e);
    }
  }, [data?.taskProgressItem, navigate, location.pathname]);

  const httpLink = new HttpLink({ uri: data.ENV.GRAPHQL_API_URL });
  const authLink = new ApolloLink((operation, forward) => {
    let userDetails = StorageService.getItem("user_details");
    userDetails =
    typeof userDetails === "object" ? userDetails : JSON.parse(userDetails);
    const token = userDetails?.accessToken || "";

    operation.setContext({
      headers: {
        authorization: token
      }
    });
    return forward(operation);
  });

  const client = new ApolloClient({
    link: authLink.concat(httpLink),
    cache: new InMemoryCache()
  });
  const [isOnline, setIsOnline, isOnlineRef] = useStateRef(data?.isOnline);

  useEffect(() => {
    let status = isUserLogin();
    setIsLogin(status);
    if (location?.pathname?.includes("/student")) {
      let schoolName = location.pathname.replace(`${urlString}/student/`, "");
      setCookie("schoolName", schoolName);
    }
    if (
    !status &&
    !location?.pathname?.endsWith("/login") &&
    !location?.pathname?.includes("/student") &&
    !location?.pathname?.includes("/invalid-school"))
    {
      redirectToLoginPage();
    }
  }, [location]);

  useEffect(() => {
    const mathJaxScript = document.createElement("script");
    mathJaxScript.type = "text/javascript";
    mathJaxScript.async = true;
    mathJaxScript.src =
    "https://cdnjs.cloudflare.com/ajax/libs/mathjax/2.7.7/MathJax.js?config=TeX-MML-AM_CHTML";
    mathJaxScript.id = "MathJax-script";
    document?.body.appendChild(mathJaxScript);
    mathJaxScript.onload = () => {
      window.MathJax.Hub.Config({
        messageStyle: "none",
        tex2jax: {
          inlineMath: [["\\(", "\\)"]],
          processEscapes: true
        }
      });
    };
    return () => {
      document?.body.removeChild(mathJaxScript);
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem("isReload", "true");
    };

    const handleUnload = () => {
      const isReload = sessionStorage.getItem("isReload");
      sessionStorage.removeItem("isReload");
    };

    const updateOnlineStatus = () => {
      setIsOnline(navigator?.onLine);
    };

    storeImage(WifiDownImg);

    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("unload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("unload", handleUnload);
    };
  }, []);

  return (
    <Document data={data}>
      <ChakraProvider theme={Theme}>
        <ApolloProvider client={client}>
          <RootProvider>
            <TourProvider>
              {!isOnlineRef?.current && <OfflineComponent />}
              {isOnlineRef?.current &&
              <div className={styles["container"]}>
                  {isLoginRef?.current &&
                !location.pathname?.includes("/onboarding") &&
                <div className={styles["top-bar"]}>
                        <TutorProvider>
                          <AppBar />
                        </TutorProvider>
                      </div>
                }
                  <div
                  className={
                  isLoginRef?.current ?
                  location.pathname.includes("/onboarding") ?
                  styles["contentOnBoarding"] :
                  styles["content"] :
                  ""
                  }>

                    <ProfileTopMenu />
                    <EditProfile />
                    <Outlet />
                  </div>
                </div>
              }
            </TourProvider>
          </RootProvider>
        </ApolloProvider>
      </ChakraProvider>
      <script
        dangerouslySetInnerHTML={{
          __html: `window.ENV = ${JSON.stringify(data.ENV)}`
        }} />

    </Document>);

}

export default withSentry(App);